import React, { FC, useEffect, useRef, useState, useContext } from "react";
import { LibraryList } from "~/design-system";
import { Library } from "~/design-system/components/matter-relation-editor/library";
import { Editor } from "@tinymce/tinymce-react";
import styles from "./proposal-preview-page.module.css";
import clsx from "clsx";
import { Editor as TinyMCEEditor } from "tinymce";
import { mergeArraysOfObjectsByProp } from "~/helpers";
import { LoadingSkeleton } from "~/components/page-loading/skeleton/loading-skeleton";
import { useMutation, useQuery, useApolloClient } from "@apollo/client";
import { UPDATE_PROPOSAL_OPTIONS, useVpdProposalEngine } from "./hooks/use-proposal-engine";
import { filteredDesignsQuery, GET_TEXT_BLOCKS } from "~/components/docx-proposal/graphql-queries";
import { useRestApi } from "~/hooks";
import { OutputDesignType, PricingType } from "~/gql/graphql";
import { FIND_MATTER, MATTER_ASSUMPTIONS_QUERY, MATTER_EXCLUSIONS_QUERY } from "~/graphql/query";
import { SelectDesignModal } from "./select-design-modal";
import "../../components/docx-proposal/font-wrapper.module.css";
import { Userpilot } from "userpilot";
import { useTaxonomyLabel } from "~/context/taxonomy-context";
import { GlobalContext } from "~/context/global-context";
import { addOfficeMenuItem, addPracticeAreaMenuItem } from "./menu-item-registry";

const {
  ["library"]: libraryClassName,
  ["library-closed"]: libraryClosedClassName,
  ["overlay"]: overlayClassName,
  ["saving-indicator"]: savingIndicatorClassName,
} = styles as any;

type State = { id: number; content: string };

export const getBeforeandAfterParts = (content: string) => {
  // Create a temporary div element
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  // Find the target div by its id
  const targetDiv = tempDiv.querySelector("#vpd-main-content");

  if (!targetDiv) {
    return {
      before: "",
      after: "",
    };
  }

  // Extract everything before the target div
  let before = "";
  let node = targetDiv.previousSibling;
  while (node) {
    if (node instanceof Element) {
      before = node.outerHTML + before;
    }
    node = node.previousSibling;
  }

  // Extract everything after the target div
  let after = "";
  node = targetDiv.nextSibling;
  while (node) {
    if (node instanceof Element) {
      after += node.outerHTML;
    }
    node = node.nextSibling;
  }

  return {
    before,
    after,
  };
};

type Props = { matterId: number; pricingType: PricingType };
export const ProposalPreviewPage: FC<Props> = ({ matterId, pricingType }) => {
  const taxonomy = useTaxonomyLabel();
  const FEE_EARNER_LABEL = taxonomy.FeeEarner;
  const [open, setOpen] = React.useState(true);
  const restApi = useRestApi();
  const downloadRef = useRef<any>(null);
  const [editorLoading, setEditorLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editorcss, setEditorCss] = useState("");
  const [showSelectDesignModal, setShowSelectDesignModal] = useState(false);

  const [items, setItems] = useState<State[]>([]);

  const client = useApolloClient();

  const { enableWordExport } = useContext(GlobalContext);

  const { refetch, isLoading, updateProposalHtml, initialCompiledContent, offices, practiceAreas } =
    useVpdProposalEngine(matterId);

  const { data: activeDesignTemplates } = useQuery(filteredDesignsQuery, {
    variables: {
      filter: {
        type: { equals: OutputDesignType.Proposal },
        active: { equals: true },
      },
    },
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    initialCompiledContent?.outputTemplateId ?? null
  );

  const handleSetEditorCss = (removeMargin: boolean) => {
    const newCss = `
      @import url('https://fonts.googleapis.com/css2?family=Carlito:ital,wght@0,400;0,700;1,400;1,700&display=swap');
      body {
        padding: ${removeMargin ? "0px" : "30px 96px 30px 96px"};
        overflow-x: hidden;
      }
      body::before,
      body::after {
        display: none !important;
      }
      .mce-content-body::before,
      .mce-content-body::after {
        display: none !important;
      }
      div[data-mce-bogus]::before,
      div[data-mce-bogus]::after {
        display: none !important;
      }
      [data-mce-caret] {
        display: none !important;
      }
      .mce-visual-caret {
        display: none !important;
      }
      ol {
        margin: 0 !important;
        padding: 0;
      }
      ol > li {
        position: relative;
        padding-left: 35px;
      }
      .page-break {
        background-color: red !important;
      }
      .dashed-line {
        border-bottom: 2px dashed #000;
        margin-bottom: 500px;

      }


    `;

    setEditorCss(newCss);

    if (editorRef.current) {
      const editor = editorRef.current;
      const iframe = editor.iframeElement;
      const iframeDocument = iframe?.contentDocument;

      if (iframeDocument) {
        // Remove existing style elements
        const existingStyles = iframeDocument.querySelectorAll("style");
        existingStyles.forEach((style) => style.remove());

        // Add new style element
        const styleElement = iframeDocument.createElement("style");
        styleElement.textContent = newCss;
        iframeDocument.head.appendChild(styleElement);

        // Force refresh
        iframe.style.display = "none";
        iframe.offsetHeight; // Force reflow
        iframe.style.display = "";
      }
    }
    setEditorLoading(false);
  };

  useEffect(() => {
    setEditorLoading(true);
    const removeMargin = activeDesignTemplates?.filteredDesigns?.find(
      (x) => x.id === selectedTemplateId
    )?.removeTemplateMargin;

    if (removeMargin != undefined) {
      handleSetEditorCss(removeMargin);
    }
  }, [selectedTemplateId, activeDesignTemplates, isLoading]);

  useEffect(() => {
    setSelectedTemplateId(initialCompiledContent?.outputTemplateId ?? null);
  }, [initialCompiledContent?.outputTemplateId]);

  const currentMatterInCache = client.readQuery({
    query: FIND_MATTER,
    variables: { id: matterId },
  });

  const currentAssumptionsInCache = client.readQuery({
    query: MATTER_ASSUMPTIONS_QUERY,
    variables: { input: matterId },
  });

  const currentExclusionsInCache = client.readQuery({
    query: MATTER_EXCLUSIONS_QUERY,
    variables: { input: matterId },
  });

  useEffect(() => {
    refetch();
  }, [
    currentMatterInCache?.findMatter,
    currentExclusionsInCache?.matterExclusions,
    currentAssumptionsInCache?.matterAssumptions,
  ]);

  const [updateProposalOptions] = useMutation(UPDATE_PROPOSAL_OPTIONS);

  const { data: allTextBlocks } = useQuery(GET_TEXT_BLOCKS, {
    variables: { matterId },
    fetchPolicy: "no-cache",
  });

  const templatesItems = allTextBlocks?.getTextBlocks;

  const [hasChanges, setHasChanges] = useState(false);

  const editorRef = useRef<TinyMCEEditor | null>(null);

  const handleEditorInit = (editor: TinyMCEEditor) => {
    editorRef.current = editor;
    setEditorLoading(false);
  };

  const handleSave = async () => {
    if (!editorRef.current || !initialCompiledContent || !hasChanges) return;

    setIsSaving(true);

    const htmlContent = editorRef.current.getContent({ format: "html" });
    const parts = getBeforeandAfterParts(htmlContent);

    try {
      await updateProposalHtml({
        variables: {
          update: {
            designTemplateId: initialCompiledContent.outputTemplateId,
            matterId,
            htmlContent,
            parts: [
              { html: parts.before, before: true },
              { html: parts.after, after: true },
            ],
          },
        },
      });

      editorRef.current.focus(false);
      const containerElement = document.querySelector(`.${styles.container}`);
      if (containerElement instanceof HTMLElement) {
        containerElement.focus();
      }

      await refetch();
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to save changes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = async (event: MouseEvent) => {
      if (
        editorRef.current &&
        !editorRef.current.getContainer().contains(event.target as Node) &&
        hasChanges
      ) {
        event.preventDefault();
        event.stopPropagation();
        await handleSave();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [matterId, updateProposalHtml, refetch, initialCompiledContent, hasChanges]);

  return (
    <>
      <div className={styles.container} tabIndex={-1}>
        <div className={clsx(libraryClassName, open || libraryClosedClassName)}>
          <Library
            libraryText="Text Blocks"
            showClose
            content={
              <LibraryList
                labels={{
                  emptyText: "No Text Blocks available.",
                  loadingText: "Loading Text Blocks...",
                }}
                itemKeyProperty="id"
                itemTextProperty="title"
                loading={false}
                items={templatesItems || []}
                selectedItems={items}
                isSelectedFunction={(item) => {
                  return items.some((i) => i.id === item.id);
                }}
                copyLibraryItem={async (item) => {
                  if (editorRef.current) {
                    editorRef.current.execCommand("mceInsertContent", false, item.content);
                    setItems((curr) => mergeArraysOfObjectsByProp(curr, [item], "id"));
                  }
                }}
              />
            }
            open={open}
            setOpen={setOpen}
          />
        </div>
        <div className={styles.workspace}>
          {isLoading && <LoadingSkeleton count={11} loading={editorLoading} />}
          {!isLoading && !!editorcss && (
            <div style={{ height: "100%", maxWidth: "100%" }}>
              <Editor
                key={"proposal-preview"}
                id={"proposal-preview"}
                scriptLoading={{ async: true, defer: true }}
                initialValue={initialCompiledContent?.html || ""}
                tinymceScriptSrc={`https://assets.vpd.law/vpd-ui/tinymce/js/tinymce/tinymce.min.js`}
                onInit={(_, ed) => {
                  handleEditorInit(ed);
                }}
                init={{
                  //@ts-ignore
                  license_key: "gpl",
                  promotion: false,
                  font_family_formats:
                    "Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Calibri=Carlito; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Oswald=oswald; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats",
                  menu: {
                    custom: {
                      title: "Proposal Options",
                      items: "includeTasks showFeeEarners showRates includeDiagram includeGantt",
                    },
                    downloadPdf: {
                      title: "Export Proposal",
                      items: enableWordExport ? "downloadPdf downloadWord" : "downloadPdf",
                    },
                    changeDesign: {
                      title: `Change Design`,
                      items: "changeDesign changeOffice changePracticeArea",
                    },
                  },
                  verify_html: false,
                  elementpath: false,
                  branding: false,
                  lists_indent_on_tab: false,
                  extended_valid_elements:
                    "svg[*],defs[*],g[*],path[*],a[href|target=_blank],a[*],div[class|style|width|display]",
                  allow_unsafe_link_target: true,
                  indent_use_margin: true,
                  advlist_number_styles: "decimal",
                  noneditable_class: "noedit",
                  highlight_on_focus: false,
                  editable_root: true,
                  editable_class: "editable",
                  content_css: "document",
                  browser_spellcheck: true,
                  content_style: editorcss,
                  pagebreak_separator: `<div style="page-break-before:always">&nbsp;</div> `,
                  newline_behavior: "invert",

                  setup: (ed) => {
                    console.log("Setting up");
                    let toggleTasks = initialCompiledContent?.proposal.includeTasks || false;
                    let toggleFeeEarners =
                      initialCompiledContent?.proposal.includeFeeEarnersInProposalOutput || false;
                    let toggleRates =
                      initialCompiledContent?.proposal.includeRatesInProposalOutput || false;
                    let toggleDiagram = initialCompiledContent?.proposal.feeRiskInclude || false;
                    let toggleGantt = initialCompiledContent?.proposal.ganttInclude || false;

                    ed.on("BeforeSetContent", function (e) {
                      if (!e.content.startsWith('<div class="noedit">')) {
                        e.content = '<div class="noedit">' + e.content + "</div>";
                      }
                    });

                    ed.on("GetContent", function (e) {
                      if (e.content && !e.content.startsWith('<div class="noedit">')) {
                        e.content = '<div class="noedit">' + e.content + "</div>";
                      }
                    });

                    if (enableWordExport) {
                      ed.ui.registry.addMenuItem("downloadWord", {
                        text: "Download as Word",
                        onAction: async () => {
                          const menuItem = ed.ui.registry.getAll();
                          if (!menuItem.menuItems.changedesign.meta) return;
                          const designId = menuItem.menuItems.changedesign.meta.designId;

                          if (!selectedTemplateId) return;
                          ed.setProgressState(true);
                          Userpilot.track("proposal_export_word");
                          const pdfDownload = await restApi.downloadProposalPdf(
                            ed.getContent({ format: "html" }),
                            matterId,
                            designId,
                            "word"
                          );
                          await pdfDownload.download(downloadRef.current);
                          ed.setProgressState(false);
                        },
                      });
                    }

                    ed.ui.registry.addMenuItem("downloadPdf", {
                      text: "Download as PDF",
                      onAction: async () => {
                        const menuItem = ed.ui.registry.getAll();

                        const changeDesignItem = menuItem.menuItems.changedesign;

                        const designId = changeDesignItem?.meta?.designId;

                        if (!designId) return;

                        if (!selectedTemplateId) return;
                        ed.setProgressState(true);
                        Userpilot.track("proposal_export_pdf");
                        const pdfDownload = await restApi.downloadProposalPdf(
                          ed.getContent({ format: "html" }),
                          matterId,
                          designId
                        );
                        await pdfDownload.download(downloadRef.current);
                        ed.setProgressState(false);
                      },
                    });

                    ed.ui.registry.addToggleMenuItem("changeDesign", {
                      text: `Change Design (${
                        initialCompiledContent?.outputTemplateName ?? "Default"
                      })`,
                      meta: {
                        designId: selectedTemplateId,
                      },
                      onAction: () => {
                        setShowSelectDesignModal(true);
                      },
                    });

                    if (offices?.officeLocations.length) {
                      addOfficeMenuItem(
                        ed,
                        offices?.officeLocations,
                        matterId,
                        refetch,
                        updateProposalHtml,
                        initialCompiledContent?.selectedOfficeId,
                        selectedTemplateId
                      );
                    }

                    if (practiceAreas?.organisationalUnits.length) {
                      addPracticeAreaMenuItem(
                        ed,
                        practiceAreas,
                        updateProposalHtml,
                        matterId,
                        refetch,
                        initialCompiledContent?.selectedOrganisationalUnitId,
                        selectedTemplateId
                      );
                    }

                    ed.ui.registry.addToggleMenuItem("includeTasks", {
                      text: "Include Tasks",
                      onAction: () => {
                        ed.setProgressState(true);
                        updateProposalOptions({
                          variables: {
                            updateOptions: {
                              matterId,
                              includeTasks: !toggleTasks,
                            },
                          },
                          onCompleted: () => {
                            toggleTasks = !toggleTasks;
                            ed.setProgressState(false, 750);
                            refetch();
                          },
                        });
                      },
                      onSetup: (api) => {
                        api.setActive(toggleTasks);
                      },
                    });
                    ed.ui.registry.addToggleMenuItem("showFeeEarners", {
                      text: `Show ${FEE_EARNER_LABEL}s`,
                      onAction: () => {
                        ed.setProgressState(true);
                        updateProposalOptions({
                          variables: {
                            updateOptions: {
                              matterId,
                              showFeeEarners: !toggleFeeEarners,
                            },
                          },
                          onCompleted: () => {
                            toggleFeeEarners = !toggleFeeEarners;
                            ed.setProgressState(false, 750);
                            refetch();
                          },
                        });
                      },
                      onSetup: (api) => {
                        api.setActive(toggleFeeEarners);
                      },
                    });
                    ed.ui.registry.addToggleMenuItem("showRates", {
                      text: "Show Rates",
                      onAction: () => {
                        ed.setProgressState(true);
                        updateProposalOptions({
                          variables: {
                            updateOptions: {
                              matterId,
                              showRates: !toggleRates,
                            },
                          },
                          onCompleted: () => {
                            toggleRates = !toggleRates;
                            ed.setProgressState(false, 750);
                            refetch();
                          },
                        });
                      },
                      onSetup: (api) => {
                        api.setActive(toggleRates || false);
                      },
                    });

                    if (pricingType === PricingType.EstimatedRange) {
                      ed.ui.registry.addToggleMenuItem("includeDiagram", {
                        text: "Include Fee Risk Diagram",
                        onAction: () => {
                          ed.setProgressState(true);
                          updateProposalOptions({
                            variables: {
                              updateOptions: {
                                matterId,
                                includeDiagram: !toggleDiagram,
                              },
                            },
                            onCompleted: () => {
                              toggleDiagram = !toggleDiagram;
                              ed.setProgressState(false, 750);
                              refetch();
                              if (toggleDiagram) {
                                // Scroll to diagram
                                const diagram = ed.getBody().querySelector("#diagram");
                                diagram?.scrollIntoView({ behavior: "smooth" });
                              }
                            },
                          });
                        },
                        onSetup: (api) => {
                          api.setEnabled(initialCompiledContent?.proposal.feeRiskValid || false);
                          api.setActive(toggleDiagram || false);
                        },
                      });
                    }

                    ed.ui.registry.addToggleMenuItem("includeGantt", {
                      text: "Include Gantt Chart",
                      onAction: () => {
                        ed.setProgressState(true);
                        updateProposalOptions({
                          variables: {
                            updateOptions: {
                              matterId,
                              includeGantt: !toggleGantt,
                            },
                          },
                          onCompleted: () => {
                            toggleGantt = !toggleGantt;
                            ed.setProgressState(false, 750);
                            refetch();
                            if (toggleGantt) {
                              // Scroll to Gantt Chart
                              const gantt = ed.getBody().querySelector("#ganttChart");
                              gantt?.scrollIntoView({ behavior: "smooth" });
                            }
                          },
                        });
                      },
                      onSetup: (api) => {
                        api.setEnabled(initialCompiledContent?.proposal.ganttAvailable || false);
                        api.setActive(toggleGantt || false);
                      },
                    });

                    ed.on("keydown", function (event) {
                      if (event.key == "Tab") {
                        ed.execCommand("Indent");

                        event.preventDefault();
                        return false;
                      }
                    });

                    ed.on("keyup", function () {
                      setHasChanges(true);
                    });
                  },
                  height: "100%",
                  width: "100%",
                  resize: false,
                  menubar:
                    "view edit insert format help custom changeDesign downloadPdf" +
                    (enableWordExport ? " downloadWord" : ""),
                  plugins:
                    "code pagebreak anchor autolink charmap image link lists advlist media searchreplace table",
                  toolbar:
                    "pagebreak undo redo | fontfamily | fontsizeinput | bold italic underline strikethrough | image table  | align lineheight | numlist bullist indent outdent | charmap | removeformat",
                  ai_request: (
                    request: any,
                    respondWith: { string: (arg0: () => Promise<void>) => any }
                  ) =>
                    respondWith.string(() => Promise.reject("See docs to implement AI Assistant")),
                }}
              />
            </div>
          )}
        </div>
        <a ref={(e) => { downloadRef.current = e; }} />
      </div>
      {(activeDesignTemplates?.filteredDesigns?.length ?? 0) > 0 && (
        <SelectDesignModal
          onSelect={async (designId) => {
            setSelectedTemplateId(designId);
            const newTemplate = activeDesignTemplates?.filteredDesigns?.find(
              (x) => x.id === designId
            );
            if (!editorRef.current || !newTemplate) return;

            editorRef.current.ui.registry.addToggleMenuItem("changeDesign", {
              text: `Change Design (${newTemplate?.title ?? "Default"})`,
              meta: {
                designId,
              },
              onAction: () => {
                setShowSelectDesignModal(true);
              },
            });
            const htmlContent = editorRef.current?.getContent({ format: "html" });
            const parts = getBeforeandAfterParts(htmlContent);
            await updateProposalHtml({
              variables: {
                update: {
                  designTemplateId: designId,
                  matterId,
                  htmlContent,
                  parts: [
                    {
                      html: parts.before,
                      before: true,
                    },
                    {
                      html: parts.after,
                      after: true,
                    },
                  ],
                  designChanged: true,
                },
              },
              onCompleted: () => {
                refetch().then((response) => {
                  const updatedHtml = response.data.proposalByMatterId.html;
                  if (editorRef.current) {
                    editorRef.current.setContent(updatedHtml);
                  }
                });
              },
            });
          }}
          close={() => setShowSelectDesignModal(false)}
          open={showSelectDesignModal}
        />
      )}
      {isSaving && hasChanges && (
        <div className={overlayClassName}>
          <div className={savingIndicatorClassName}>Saving changes...</div>
        </div>
      )}
    </>
  );
};
